import { Link } from 'wouter'
import { system404, lorebookBodies, type CelestialBody, type Faction } from '../lore'
import { Markdown } from '../components/Markdown'
import { LorebookShell, LoreSection, type Chapter } from '../components/LorebookShell'

const typeLabel: Record<string, string> = {
  star: 'Star',
  molten: 'Molten core world',
  rocky: 'Rocky planet',
  terrestrial: 'Terrestrial',
  'gas-giant': 'Gas giant',
  frozen: 'Frozen world',
  dwarf: 'Dwarf planet',
  debris: 'Debris field',
  'asteroid-belt': 'Asteroid belt',
}

function BodyCard({ body }: { body: CelestialBody }) {
  return (
    <Link
      href={`/lorebook/${body.id}`}
      className="group block rounded-2xl border border-edge bg-void-2/60 p-4 transition hover:border-signal/50 hover:bg-void-2"
    >
      <div className="flex items-center gap-3">
        <span
          className="h-3 w-3 shrink-0 rounded-full"
          style={{ background: body.render.color, boxShadow: `0 0 12px ${body.render.color}` }}
        />
        <div className="min-w-0">
          <div className="font-display text-lg text-white group-hover:text-signal">{body.name}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            {typeLabel[body.type] ?? body.type}
          </div>
        </div>
      </div>
      {body.tagline && <p className="mt-3 line-clamp-2 text-sm text-slate-400">{body.tagline}</p>}
    </Link>
  )
}

function FactionCard({ faction }: { faction: Faction }) {
  return (
    <Link
      href={`/lorebook/${faction.id}`}
      className="group block rounded-2xl border border-edge bg-void-2/60 p-4 transition hover:border-thelxis/50 hover:bg-void-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-display text-lg text-white group-hover:text-thelxis">{faction.name}</div>
        {faction.concept && (
          <span className="rounded-full border border-edge px-2 py-0.5 text-[10px] uppercase text-slate-500">
            concept
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">{faction.tagline}</p>
    </Link>
  )
}

/** The lorebook landing page: the system overview as a readable document. */
export function Lorebook() {
  const chapters: Chapter[] = [
    { id: 'overview', label: 'Overview' },
    ...(system404.anomalies.length ? [{ id: 'anomalies', label: 'Anomalies' }] : []),
    ...(system404.sosSignal ? [{ id: 'sos', label: 'The SOS Signal' }] : []),
    ...(system404.comms ? [{ id: 'comms', label: 'Comms' }] : []),
    ...(system404.mechs ? [{ id: 'mechs', label: 'Extravehicular Units' }] : []),
    ...(system404.economy ? [{ id: 'economy', label: 'Economy' }] : []),
    ...(system404.livingNotes ? [{ id: 'living', label: 'Living in the Lie' }] : []),
    ...(system404.factionRelations ? [{ id: 'relations', label: 'Factions' }] : []),
    { id: 'bodies', label: 'Bodies' },
  ]

  return (
    <LorebookShell activeId={null} title="System 404" chapters={chapters}>
      <header id="overview" className="scroll-mt-24">
        {/* the logo carries the title text; the h1 + alt keep it readable to
            screen readers and crawlers */}
        <h1>
          <img
            src="/logo.png"
            alt="System 404: Signal Lost"
            width={1699}
            height={624}
            className="w-full"
          />
        </h1>
        <p className="mt-3 text-slate-500">{system404.tagline}</p>
      </header>

      <div className="md mt-6 text-slate-300">
        <Markdown>{system404.overview}</Markdown>
      </div>

      {system404.anomalies.length > 0 && (
        <LoreSection id="anomalies" title="Anomalies">
          <div className="divide-y divide-edge">
            {system404.anomalies.map((a, i) => (
              <div key={i} className="md py-4 text-sm first:pt-0 last:pb-0">
                <Markdown>{a}</Markdown>
              </div>
            ))}
          </div>
        </LoreSection>
      )}

      {system404.sosSignal && (
        <LoreSection id="sos" title="The SOS Signal">
          <div className="md">
            <Markdown>{system404.sosSignal}</Markdown>
          </div>
        </LoreSection>
      )}

      {system404.comms && (
        <LoreSection id="comms" title="Comms">
          <div className="md">
            <Markdown>{system404.comms}</Markdown>
          </div>
        </LoreSection>
      )}

      {system404.mechs && (
        <LoreSection id="mechs" title="Extravehicular Units">
          <div className="md">
            <Markdown>{system404.mechs}</Markdown>
          </div>
        </LoreSection>
      )}

      {system404.economy && (
        <LoreSection id="economy" title="Economy">
          <div className="md">
            <Markdown>{system404.economy}</Markdown>
          </div>
        </LoreSection>
      )}

      {system404.livingNotes && (
        <LoreSection id="living" title="Living in the Lie">
          <div className="md">
            <Markdown>{system404.livingNotes}</Markdown>
          </div>
        </LoreSection>
      )}

      {system404.factionRelations && (
        <LoreSection id="relations" title="How the Factions Relate">
          <div className="md">
            <Markdown>{system404.factionRelations}</Markdown>
          </div>
        </LoreSection>
      )}

      <LoreSection id="bodies" title="Bodies & Factions">
        <p className="text-sm text-slate-400">
          Browse every body and faction, or open the{' '}
          <Link href="/" className="text-signal hover:underline">
            interactive map
          </Link>{' '}
          to see it all in motion.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {lorebookBodies.map((b) => (
            <BodyCard key={b.id} body={b} />
          ))}
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {system404.factions.map((f) => (
            <FactionCard key={f.id} faction={f} />
          ))}
        </div>
      </LoreSection>
    </LorebookShell>
  )
}
